import { useState, useRef, useEffect } from "react";
import { Check, Pencil } from "lucide-react";
import { toast } from "sonner";

/**
 * InlineEditSelect — click a status badge / text → switch to <select> → save on change → revert on Escape.
 *
 * Props:
 * - value: current string
 * - options: [["value","label"], ...]
 * - onSave(newValue): async function — should return rejected promise on error
 * - render(value): optional — custom render of static state (e.g. <StatusBadge/>)
 * - testid: optional
 */
export function InlineEditSelect({ value, options, onSave, render, testid, disabled = false }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);

  useEffect(() => { setVal(value); }, [value]);
  useEffect(() => {
    if (editing) ref.current?.focus();
  }, [editing]);

  async function commit(newV) {
    if (newV === value) { setEditing(false); return; }
    setBusy(true);
    try {
      await onSave(newV);
      setVal(newV);
      setEditing(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen");
      setVal(value);
      setEditing(false);
    } finally { setBusy(false); }
  }

  if (disabled) {
    return <span data-testid={testid}>{render ? render(val) : <span className="text-zinc-300">{val}</span>}</span>;
  }

  if (!editing) {
    return (
      <button
        onClick={(e) => { e.stopPropagation(); setEditing(true); }}
        data-testid={testid}
        className="group inline-flex items-center gap-1.5 cursor-pointer hover:bg-zinc-800/40 -mx-1 px-1 rounded"
        title="Klicken zum Bearbeiten"
      >
        {render ? render(val) : <span className="text-zinc-300">{val}</span>}
        <Pencil className="w-3 h-3 text-zinc-700 group-hover:text-zinc-400 opacity-0 group-hover:opacity-100 transition-opacity" />
      </button>
    );
  }
  return (
    <select
      ref={ref}
      value={val}
      disabled={busy}
      onChange={(e) => commit(e.target.value)}
      onBlur={() => setEditing(false)}
      onKeyDown={(e) => { if (e.key === "Escape") { setVal(value); setEditing(false); } }}
      onClick={(e) => e.stopPropagation()}
      data-testid={testid ? `${testid}-select` : undefined}
      className="px-2 py-1 bg-zinc-950 border border-blue-600 rounded text-xs text-zinc-100"
    >
      {options.map(([v, l]) => (<option key={v} value={v}>{l}</option>))}
    </select>
  );
}

/**
 * InlineEditText — click text → switch to <input> → save on Enter / blur → revert on Escape.
 */
export function InlineEditText({ value, onSave, type = "text", placeholder, testid, className = "", parse }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value ?? "");
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);

  useEffect(() => { setVal(value ?? ""); }, [value]);
  useEffect(() => { if (editing) { ref.current?.focus(); ref.current?.select?.(); } }, [editing]);

  async function commit() {
    const out = parse ? parse(val) : val;
    if (out === value) { setEditing(false); return; }
    setBusy(true);
    try {
      await onSave(out);
      setEditing(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen");
      setVal(value ?? "");
      setEditing(false);
    } finally { setBusy(false); }
  }

  if (!editing) {
    return (
      <button
        onClick={(e) => { e.stopPropagation(); setEditing(true); }}
        data-testid={testid}
        className={`group inline-flex items-center gap-1.5 cursor-pointer hover:bg-zinc-800/40 -mx-1 px-1 rounded ${className}`}
        title="Klicken zum Bearbeiten"
      >
        <span className="text-zinc-300">{value || <span className="text-zinc-600 italic">leer</span>}</span>
        <Pencil className="w-3 h-3 text-zinc-700 group-hover:text-zinc-400 opacity-0 group-hover:opacity-100 transition-opacity" />
      </button>
    );
  }
  return (
    <span className="inline-flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
      <input
        ref={ref}
        type={type}
        value={val}
        disabled={busy}
        placeholder={placeholder}
        onChange={(e) => setVal(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit();
          if (e.key === "Escape") { setVal(value ?? ""); setEditing(false); }
        }}
        data-testid={testid ? `${testid}-input` : undefined}
        className="px-2 py-1 w-32 bg-zinc-950 border border-blue-600 rounded text-xs text-zinc-100"
      />
      <Check className="w-3.5 h-3.5 text-emerald-400" />
    </span>
  );
}
